import { InfoIcon } from '@/assets/icons/info-icons/info';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import FileUpload from '@/components/basicComponents/fileUpload';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import { DropFileArea } from '@/components/dueDiligence-page/dueDiligenceRecord/dropFileArea';
import { ErrorText } from '@/components/basicComponents/input';
import { useErrorMessages } from '@/hooks/i18n';
import { themeVar } from '@/styles/theme';
import axios from 'axios';
import { Dispatch, SetStateAction, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';

interface IForm {
  file: File | null;
}

const defaultFormValues: IForm = {
  file: null
};

const allowedFileExtensions = ['DOCX'];

interface UploadCustomExportTemplateProps {
  isOpen: boolean;
  funnelId: string;
  setCustomExportTemplateName: Dispatch<SetStateAction<string>>;
  onClose: () => void;
}

const UploadCustomExportTemplate = ({ isOpen, funnelId, setCustomExportTemplateName, onClose }: UploadCustomExportTemplateProps) => {
  const { default: defaultErrorMessage } = useErrorMessages();
  const { control, setValue, handleSubmit } = useForm<IForm>({ defaultValues: defaultFormValues });

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [uploadFilesLoading, setUploadFilesLoading] = useState(false);
  const [drag, setDrag] = useState(false);

  const onSave = async (data: IForm) => {
    try {
      setUploadFilesLoading(true);
      setErrorMessage(null);
      const { file } = data;

      const formData = new FormData();
      formData.append('file', file);

      const { data: uploadDocxTemplateData } = await axios.put(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/due-diligence-funnel/${funnelId}/upload-docx-template`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (uploadDocxTemplateData?.docxTemplateStorageKey) {
        const fileName = uploadDocxTemplateData.docxTemplateStorageKey?.split('/')?.pop();
        setCustomExportTemplateName(fileName);
      }

      onClose();
    } catch (error) {
      setErrorMessage(defaultErrorMessage);
      console.error(error);
    } finally {
      setUploadFilesLoading(false);
    }
  };

  const onDropHandler = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const uploadFiles = e.dataTransfer.files;
    setDrag(false);
    setValue('file', uploadFiles[0]);
  };

  const handleUploadFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const uploadFiles = e.target.files;
    setValue('file', uploadFiles[0]);
  };

  return (
    <ModalWrappedContent
      title="Upload Custom Export Template"
      isOpen={isOpen}
      onClose={onClose}
      showCloseIcon={!uploadFilesLoading}
      showCancelButton={!uploadFilesLoading}
      isUseOutsideClick={!uploadFilesLoading}
      confirmButton={{ name: 'Confirm', onClick: handleSubmit(onSave), disabled: uploadFilesLoading }}
    >
      {uploadFilesLoading ? (
        <ModalLoader />
      ) : (
        <>
          {errorMessage && (
            <ErrorBannerWrapper>
              <Banner
                icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
                title="Error"
                description={errorMessage}
                bgColor={themeVar('context.error')}
              />
            </ErrorBannerWrapper>
          )}
          <ModalBody>
            <Banner
              title=""
              description="Populate a custom DOCX template by using tokens that map to the fields defined in this funnel.  Once uploaded, you can download data for individual records that use this template."
              icon={<InfoIcon width={26} height={26} fill={themeVar('font.base')} />}
              bgColor={themeVar('layer.base')}
              color={themeVar('font.base')}
              dividerColor={themeVar('border.base')}
            />

            <Controller
              name="file"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: 'File is required'
                }
              }}
              render={({ field: { value: file }, fieldState: { error } }) => (
                <>
                  {file ? (
                    <FileUpload
                      type={file.type.includes('image') ? 'image' : 'document'}
                      imageSrc={URL.createObjectURL(file)}
                      fileName={file.name}
                      deleteFile={() => setValue('file', null)}
                    />
                  ) : (
                    <div>
                      <DropFileArea
                        id="custom-export-template"
                        handleUploadFile={handleUploadFile}
                        onDropHandler={onDropHandler}
                        drag={drag}
                        setDrag={setDrag}
                        fileExtensions={allowedFileExtensions}
                        maxSize={10}
                      />
                      {error?.message && <ErrorText size="md">{error.message}</ErrorText>}
                    </div>
                  )}
                </>
              )}
            />
          </ModalBody>
        </>
      )}
    </ModalWrappedContent>
  );
};

export default UploadCustomExportTemplate;

const ModalBody = styled.div<{ alignItems?: string }>`
  display: flex;
  flex-direction: column;
  align-items: ${({ alignItems }) => alignItems || 'stretch'};
  gap: 10px;
`;

const ErrorBannerWrapper = styled.div`
  margin-bottom: 10px;
`;
